/**
 * useAgentEvals — named hook for agent evaluation rollup data (FR-009, FR-010).
 *
 * Returns QueryState<EvalsView> discriminated union with 60-second
 * stale-while-revalidate caching semantics.
 *
 * The hook call is unconditional (Rules of Hooks). When
 * useSubscriptionActive() returns false, the literal "skip" is passed
 * to useQuery to prevent a network round-trip.
 *
 * @see specs/prd.md Section 7.4, FR-010
 * @see shared/types/evals.ts
 */
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useSubscriptionActive } from "../../components/layout/SubscriptionScope";
import type { QueryState } from "../queryState";
import type { EvalsView, EvalRollup } from "../../../../../shared/types/evals";

/**
 * Hook result type — QueryState<EvalsView>.
 */
export type AgentEvalsResult = QueryState<EvalsView>;

/**
 * Named hook returning the latest agent evaluation rollup.
 *
 * The result follows the 5-branch QueryState discriminated union:
 * loading | empty | ready | error | forbidden.
 */
export function useAgentEvals(): AgentEvalsResult {
  const isActive = useSubscriptionActive();

  const raw = useQuery(
    api.composites.agentEvals.agentEvals,
    isActive ? {} : "skip",
  );

  // ── Loading ────────────────────────────────────────────────────────
  if (raw === undefined) {
    return { status: "loading" };
  }

  // ── Empty (no rollups computed yet) ────────────────────────────────
  if (raw.latest === null) {
    return { status: "empty" };
  }

  // ── Ready ──────────────────────────────────────────────────────────
  const latest: EvalRollup = raw.latest;
  const view: EvalsView = { latest };

  return { status: "ready", data: view };
}
